/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    TOAST_MSG_UPDATE_IRIT_2026
 * Date:         2026-01-27
 * Modified By:  Cursor
 *
 * Description:
 * Updated Toastify messages to Irit's improved user-friendly versions.
 *
 * File Size Check:
 * - Current: 738 lines
 * - Adding: ~6 lines
 * - Decision: Proceed
 *
 * Typography Compliance: N/A (notification messages only)
 * Related Files: utils/notifications.ts
 */

/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    SMART_FILTER_FIGMA_ALIGN
 * Date:         2026-01-27
 * Modified By:  Cursor
 *
 * Description:
 * Refactored from step-based wizard to modern vertical layout with collapsible sections
 * matching Figma design. All filter sections (Type, Categories, Keywords, Manufacturers)
 * are now visible at once, stacked vertically with expand/collapse functionality.
 *
 * Related Screens / Modules:
 * - client/src/features/setup/alerts/SmartFiltersWizard.tsx
 * - client/src/features/setup/alerts/SmartFiltersWizard.scss (NEW)
 */

import React, { useState, useEffect } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { Container, Row, Col, Button, Card, Badge, Breadcrumb, Form as BootstrapForm } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheckCircle,
  faChevronDown,
  faChevronUp,
  faXmark,
  faCheck,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate, Link } from "react-router-dom";
import { CATEGORY_OPTIONS } from "../../../constants/inventoryOptions";
import {
  createAlertProfile,
  updateAlertProfile,
  AlertProfile,
} from "../../../services/alertProfilesService";
import { notify } from "../../../utils/notifications";
// [2026-07-08] cookie-auth-fix: use cookie-based checkAuthStatus instead of deprecated getUserInfoFromToken (localStorage)
import { checkAuthStatus } from "../../../utils/authUtils";
import { helpForField, helpForOption } from "../../../constants/alertHelp";
import "./SmartFiltersWizard.scss";

interface SmartFiltersWizardProps {
  onComplete: () => void;
  editProfile?: AlertProfile;
}

interface FormValues {
  type: string;
  categories: string[];
  keywords: string[];
  manufacturers: string[];
  name: string;
}

const validationSchema = Yup.object({
  type: Yup.string().required("Please select a type"),
  categories: Yup.array().min(0),
  keywords: Yup.array(),
  manufacturers: Yup.array(),
  name: Yup.string()
    .min(2, "Name must be at least 2 characters")
    .max(60, "Name must be at most 60 characters"),
});

// Map AlertProfile to FormValues
const mapProfileToFormValues = (profile: AlertProfile): FormValues => {
  return {
    type: profile.filters?.type || "",
    categories: profile.filters?.categories || [],
    keywords: profile.filters?.keywords || [],
    manufacturers: profile.filters?.manufacturers || [],
    name: profile.name || "",
  };
};

const SmartFiltersWizard: React.FC<SmartFiltersWizardProps> = ({ onComplete, editProfile }) => {
  const navigate = useNavigate();

  const sanitizeUsernameForProfileName = (raw: string): string => {
    const trimmed = (raw || "").trim();
    const underscored = trimmed.replace(/\s+/g, "_");
    const safe = underscored.replace(/[^A-Za-z0-9_]/g, "");
    return safe || "User";
  };

  const generateTwoDigitSuffix = (): string => {
    const n = Math.floor(Math.random() * 100); // 0-99
    return String(n).padStart(2, "0");
  };

  // [2026-07-08] cookie-auth-fix: generate suffix once so the name doesn't churn when the
  // async auth check resolves
  const [nameSuffix] = useState<string>(() => generateTwoDigitSuffix());

  const buildDefaultProfileName = (firstName?: string): string => {
    const base = sanitizeUsernameForProfileName(firstName || "User");
    // Enforce max length: 60 chars total, suffix is "_XX" (3 chars)
    const maxBaseLen = 57;
    const clippedBase = base.length > maxBaseLen ? base.slice(0, maxBaseLen) : base;
    return `${clippedBase}_${nameSuffix}`;
  };

  // [2026-07-08] cookie-auth-fix: seed synchronously with a fallback, then refine with the real
  // first name from the cookie-based auth check. enableReinitialize flows it into the form.
  const [prefilledName, setPrefilledName] = useState<string>(() =>
    editProfile ? "" : buildDefaultProfileName()
  );

  useEffect(() => {
    if (editProfile) return;
    checkAuthStatus().then((userInfo) => {
      if (userInfo?.firstName) {
        setPrefilledName(buildDefaultProfileName(userInfo.firstName));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editProfile]);
  
  // Determine initial values based on edit mode
  const getInitialValues = (): FormValues => {
    if (editProfile) {
      return mapProfileToFormValues(editProfile);
    }
    return {
      type: "",
      categories: [],
      keywords: [],
      manufacturers: [],
      name: prefilledName,
    };
  };

  // Determine initial expanded sections - expand sections with values when editing
  const getInitialExpandedSections = (): {
    type: boolean;
    categories: boolean;
    keywords: boolean;
    manufacturers: boolean;
  } => {
    if (editProfile) {
      const values = mapProfileToFormValues(editProfile);
      return {
        type: true, // Always expand type
        categories: values.categories.length > 0,
        keywords: values.keywords.length > 0,
        manufacturers: values.manufacturers.length > 0,
      };
    }
    return {
      type: true, // Type section expanded by default
      categories: false,
      keywords: false,
      manufacturers: false,
    };
  };

  // Collapsible section states
  const [expandedSections, setExpandedSections] = useState<{
    type: boolean;
    categories: boolean;
    keywords: boolean;
    manufacturers: boolean;
  }>(getInitialExpandedSections());

  // State for current input values (for real-time preview)
  // CRITICAL FIX: Initialize input fields with existing values when editing
  const [keywordInput, setKeywordInput] = useState<string>(() => {
    if (editProfile?.filters?.keywords && Array.isArray(editProfile.filters.keywords) && editProfile.filters.keywords.length > 0) {
      return editProfile.filters.keywords.join(", ");
    }
    return "";
  });
  const [manufacturerInput, setManufacturerInput] = useState<string>(() => {
    if (editProfile?.filters?.manufacturers && Array.isArray(editProfile.filters.manufacturers) && editProfile.filters.manufacturers.length > 0) {
      return editProfile.filters.manufacturers.join(", ");
    }
    return "";
  });
  
  // Update input fields when editProfile changes (for async loading)
  useEffect(() => {
    if (editProfile?.filters?.keywords && Array.isArray(editProfile.filters.keywords) && editProfile.filters.keywords.length > 0) {
      setKeywordInput(editProfile.filters.keywords.join(", "));
    }
    if (editProfile?.filters?.manufacturers && Array.isArray(editProfile.filters.manufacturers) && editProfile.filters.manufacturers.length > 0) {
      setManufacturerInput(editProfile.filters.manufacturers.join(", "));
    }
  }, [editProfile]);

  const handleSubmit = async (values: FormValues) => {
    try {
      // [2026-07-08] cookie-auth-fix: prefilledName already holds the resolved default (real
      // first name via cookie auth); fall back to a freshly built default if empty
      const name = values.name || prefilledName || buildDefaultProfileName();

      // CRITICAL FIX: Use input fields as the source of truth - they overwrite, not append
      // When user edits the input field, parse it and use that as the final array
      // This ensures editing the input replaces old values instead of appending
      let finalKeywords: string[] = [];
      let finalManufacturers: string[] = [];

      if (keywordInput.trim()) {
        finalKeywords = keywordInput
          .split(",")
          .map((k) => k.trim())
          .filter((k) => k); // Remove empty strings
      }

      if (manufacturerInput.trim()) {
        finalManufacturers = manufacturerInput
          .split(",")
          .map((m) => m.trim())
          .filter((m) => m); // Remove empty strings
      }

      const profileData = {
        name,
        mode: "smart" as const,
        filters: {
          type: values.type,
          categories: values.categories,
          keywords: finalKeywords,
          manufacturers: finalManufacturers,
        },
        // Preserve isActive status when editing
        ...(editProfile ? { isActive: editProfile.isActive } : { isActive: true }),
      };

      if (editProfile) {
        await updateAlertProfile(editProfile._id, profileData);
        // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
        notify.success("Alert profile updated successfully");
      } else {
        await createAlertProfile(profileData);
        // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
        notify.success("Alert profile created successfully");
      }
      // Navigate with refresh flag
      navigate("/alerts?refresh=true");
    } catch (error: any) {
      // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
      notify.error(
        error,
        editProfile ? "We couldn't update the alert profile. Please try again" : "We couldn't create the alert profile. Please try again",
      );
    }
  };

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const hasValue = (section: keyof typeof expandedSections, values: FormValues): boolean => {
    switch (section) {
      case "type":
        return !!values.type;
      case "categories":
        return values.categories.length > 0;
      case "keywords":
        return values.keywords.length > 0;
      case "manufacturers":
        return values.manufacturers.length > 0;
      default:
        return false;
    }
  };

  const handleCancel = () => {
    navigate("/alerts");
  };

  return (
    <Container fluid className="smart-filters-wizard">
      <Formik
        initialValues={getInitialValues()}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        validateOnChange
        enableReinitialize
        key={editProfile?._id || 'new'}
      >
        {({ values, errors, setFieldValue, isSubmitting }) => (
          <Form>
            {/* Breadcrumbs */}
            <Row className="mb-3">
              <Col>
                <Breadcrumb>
                  <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/" }}>
                    Home
                  </Breadcrumb.Item>
                  <Breadcrumb.Item active>Broadcast Alerts</Breadcrumb.Item>
                </Breadcrumb>
              </Col>
            </Row>

            {/* Page Title */}
            <Row className="mb-4">
              <Col>
                <h1 className="smart-filters-title">Broadcast Alerts</h1>
              </Col>
            </Row>

            {/* Alert Name Field */}
            <Row className="mb-4">
              <Col lg={8}>
                <BootstrapForm.Group>
                  <BootstrapForm.Label
                    className="fw-semibold mb-2"
                    title="A default name has been generated for this alert profile. You can edit it anytime."
                  >
                    Alert Name
                  </BootstrapForm.Label>
                  <Field
                    name="name"
                    type="text"
                    className="form-control smart-filter-input"
                    placeholder="Enter a name for this alert profile"
                  />
                  <ErrorMessage name="name" component="div" className="text-danger small mt-1" />
                  <BootstrapForm.Text className="text-muted small d-block mt-1">
                    Default format: <strong>firstName_XX</strong> (XX is a 2-digit random number)
                  </BootstrapForm.Text>
                </BootstrapForm.Group>
              </Col>
            </Row>

            <Row>
              {/* Main Content - Filter Sections */}
              <Col lg={8}>
                {/* Type Section */}
                <Card
                  className={`smart-filter-section mb-3 ${
                    expandedSections.type ? "expanded" : "collapsed"
                  } ${hasValue("type", values) ? "has-value" : ""}`}
                >
                  <Card.Header
                    className="smart-filter-section-header"
                    onClick={() => toggleSection("type")}
                  >
                    <div className="d-flex align-items-center">
                      <h5 className="mb-0 me-2">Type</h5>
                      {hasValue("type", values) && (
                        <FontAwesomeIcon
                          icon={faCheckCircle}
                          className="smart-filter-checkmark"
                        />
                      )}
                    </div>
                    <FontAwesomeIcon
                      icon={expandedSections.type ? faChevronUp : faChevronDown}
                      className="smart-filter-chevron"
                    />
                  </Card.Header>
                  {expandedSections.type && (
                    <Card.Body className="smart-filter-section-body">
                      <p className="smart-filter-description mb-3" title={helpForField("type")}>
                        What type of alerts do you want?
                      </p>
                      <div className="d-flex gap-3 flex-wrap">
                        <div
                          className={`smart-type-card ${
                            values.type === "WTB" ? "active" : ""
                          }`}
                          onClick={() => setFieldValue("type", "WTB")}
                          title={helpForOption("type", "WTB")}
                        >
                          <div className="smart-type-title">WTB</div>
                          <div className="smart-type-subtitle">Want To Buy</div>
                        </div>
                        <div
                          className={`smart-type-card ${
                            values.type === "WTS" ? "active" : ""
                          }`}
                          onClick={() => setFieldValue("type", "WTS")}
                          title={helpForOption("type", "WTS")}
                        >
                          <div className="smart-type-title">WTS</div>
                          <div className="smart-type-subtitle">Want To Sell</div>
                        </div>
                        <div
                          className={`smart-type-card ${
                            values.type === "Both" ? "active" : ""
                          }`}
                          onClick={() => setFieldValue("type", "Both")}
                          title={helpForOption("type", "Both")}
                        >
                          <div className="smart-type-title">Both</div>
                          <div className="smart-type-subtitle">Buy & Sell</div>
                        </div>
                      </div>
                      {errors.type && (
                        <div className="text-danger mt-2">{errors.type}</div>
                      )}
                    </Card.Body>
                  )}
                </Card>

                {/* Categories Section */}
                <Card
                  className={`smart-filter-section mb-3 ${
                    expandedSections.categories ? "expanded" : "collapsed"
                  } ${hasValue("categories", values) ? "has-value" : ""}`}
                >
                  <Card.Header
                    className="smart-filter-section-header"
                    onClick={() => toggleSection("categories")}
                  >
                    <div className="d-flex align-items-center">
                      <h5 className="mb-0 me-2">Categories</h5>
                      {hasValue("categories", values) && (
                        <FontAwesomeIcon
                          icon={faCheckCircle}
                          className="smart-filter-checkmark"
                        />
                      )}
                    </div>
                    <FontAwesomeIcon
                      icon={expandedSections.categories ? faChevronUp : faChevronDown}
                      className="smart-filter-chevron"
                    />
                  </Card.Header>
                  {expandedSections.categories && (
                    <Card.Body className="smart-filter-section-body">
                      <p className="smart-filter-description mb-3" title={helpForField("categories")}>
                        Choose one or more categories to monitor
                      </p>
                      <div className="d-flex flex-wrap gap-2">
                        {CATEGORY_OPTIONS.map((category) => (
                          <Badge
                            key={category}
                            className={`smart-category-badge ${
                              values.categories.includes(category) ? "active" : ""
                            }`}
                            onClick={() => {
                              const newCategories = values.categories.includes(category)
                                ? values.categories.filter((c) => c !== category)
                                : [...values.categories, category];
                              setFieldValue("categories", newCategories);
                            }}
                          >
                            {category}
                          </Badge>
                        ))}
                      </div>
                    </Card.Body>
                  )}
                </Card>

                {/* Keywords Section */}
                <Card
                  className={`smart-filter-section mb-3 ${
                    expandedSections.keywords ? "expanded" : "collapsed"
                  } ${hasValue("keywords", values) ? "has-value" : ""}`}
                >
                  <Card.Header
                    className="smart-filter-section-header"
                    onClick={() => toggleSection("keywords")}
                  >
                    <div className="d-flex align-items-center">
                      <h5 className="mb-0 me-2">Keywords</h5>
                      {hasValue("keywords", values) && (
                        <FontAwesomeIcon
                          icon={faCheckCircle}
                          className="smart-filter-checkmark"
                        />
                      )}
                    </div>
                    <FontAwesomeIcon
                      icon={expandedSections.keywords ? faChevronUp : faChevronDown}
                      className="smart-filter-chevron"
                    />
                  </Card.Header>
                  {expandedSections.keywords && (
                    <Card.Body className="smart-filter-section-body">
                      <p className="smart-filter-description mb-3" title={helpForField("keywords", "include")}>
                        Add keywords separated by commas to get alerts when they appear in a broadcast. Example: drone, camera, gimbal
                      </p>
                      <input
                        type="text"
                        className="form-control smart-filter-input"
                        placeholder="Enter keywords separated by commas"
                        value={keywordInput}
                        onChange={(e) => setKeywordInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            const value = keywordInput.trim();
                            if (value) {
                              const keywords = value
                                .split(",")
                                .map((k) => k.trim())
                                .filter((k) => k && !values.keywords.includes(k));
                              if (keywords.length > 0) {
                                setFieldValue("keywords", [...values.keywords, ...keywords]);
                                setKeywordInput("");
                              }
                            }
                          }
                        }}
                      />
                    </Card.Body>
                  )}
                </Card>

                {/* Manufacturers Section */}
                <Card
                  className={`smart-filter-section mb-3 ${
                    expandedSections.manufacturers ? "expanded" : "collapsed"
                  } ${hasValue("manufacturers", values) ? "has-value" : ""}`}
                >
                  <Card.Header
                    className="smart-filter-section-header"
                    onClick={() => toggleSection("manufacturers")}
                  >
                    <div className="d-flex align-items-center">
                      <h5 className="mb-0 me-2">Manufacturers</h5>
                      {hasValue("manufacturers", values) && (
                        <FontAwesomeIcon
                          icon={faCheckCircle}
                          className="smart-filter-checkmark"
                        />
                      )}
                    </div>
                    <FontAwesomeIcon
                      icon={expandedSections.manufacturers ? faChevronUp : faChevronDown}
                      className="smart-filter-chevron"
                    />
                  </Card.Header>
                  {expandedSections.manufacturers && (
                    <Card.Body className="smart-filter-section-body">
                      <p className="smart-filter-description mb-3" title={helpForField("manufacturers")}>
                        Add manufacturers separated by commas to get alerts when they appear in a broadcast. Example: DJI, Parrot, Autel
                      </p>
                      <input
                        type="text"
                        className="form-control smart-filter-input"
                        placeholder="Enter Manufacturers separated by commas"
                        value={manufacturerInput}
                        onChange={(e) => setManufacturerInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            const value = manufacturerInput.trim();
                            if (value) {
                              const manufacturers = value
                                .split(",")
                                .map((m) => m.trim())
                                .filter((m) => m && !values.manufacturers.includes(m));
                              if (manufacturers.length > 0) {
                                setFieldValue("manufacturers", [
                                  ...values.manufacturers,
                                  ...manufacturers,
                                ]);
                                setManufacturerInput("");
                              }
                            }
                          }
                        }}
                      />
                    </Card.Body>
                  )}
                </Card>
              </Col>

              {/* Preview Panel */}
              <Col lg={4}>
                <Card className="smart-preview-card">
                  <Card.Header>
                    <h5 className="mb-0">Preview</h5>
                  </Card.Header>
                  <Card.Body>
                    <div className="smart-preview-content">
                      <div className="mb-3">
                        <strong className="smart-preview-label">Type:</strong>
                        {values.type ? (
                          <div className="d-flex flex-wrap gap-1 mt-1">
                            {values.type === "Both" ? (
                              <>
                                <Badge className="smart-preview-badge smart-preview-badge-wtb">
                                  WTB
                                </Badge>
                                <Badge className="smart-preview-badge smart-preview-badge-wts">
                                  WTS
                                </Badge>
                              </>
                            ) : (
                              <Badge
                                className={`smart-preview-badge ${
                                  values.type === "WTB"
                                    ? "smart-preview-badge-wtb"
                                    : "smart-preview-badge-wts"
                                }`}
                              >
                                {values.type}
                              </Badge>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted">Not selected</span>
                        )}
                      </div>

                      <div className="mb-3">
                        <strong className="smart-preview-label">
                          Category ({values.categories.length}):
                        </strong>
                        {values.categories.length > 0 ? (
                          <div className="d-flex flex-wrap gap-1 mt-1">
                            {values.categories.map((cat: string) => (
                              <Badge
                                key={cat}
                                className="smart-preview-badge smart-preview-badge-category"
                              >
                                {cat}
                              </Badge>
                            ))}
                          </div>
                        ) : (
                          <span className="text-muted"> No Categories chosen</span>
                        )}
                      </div>

                      <div className="mb-3">
                        <strong className="smart-preview-label">
                          Keywords (
                          {values.keywords.length +
                            (keywordInput.trim() &&
                            keywordInput
                              .split(",")
                              .map((k) => k.trim())
                              .filter((k) => k && !values.keywords.includes(k)).length >
                              0
                              ? keywordInput
                                  .split(",")
                                  .map((k) => k.trim())
                                  .filter((k) => k && !values.keywords.includes(k)).length
                              : 0)}
                          ):
                        </strong>
                        {values.keywords.length > 0 ||
                        (keywordInput.trim() &&
                          keywordInput
                            .split(",")
                            .map((k) => k.trim())
                            .filter((k) => k && !values.keywords.includes(k)).length > 0) ? (
                          <div className="d-flex flex-wrap gap-1 mt-1">
                            {values.keywords.map((kw: string, idx: number) => (
                              <Badge key={idx} className="smart-preview-badge">
                                {kw}
                              </Badge>
                            ))}
                            {keywordInput.trim() &&
                              keywordInput
                                .split(",")
                                .map((k) => k.trim())
                                .filter((k) => k && !values.keywords.includes(k))
                                .map((kw: string, idx: number) => (
                                  <Badge
                                    key={`pending-${idx}`}
                                    className="smart-preview-badge"
                                  >
                                    {kw}
                                  </Badge>
                                ))}
                          </div>
                        ) : (
                          <span className="text-muted"> No Keywords added</span>
                        )}
                      </div>

                      <div className="mb-3">
                        <strong className="smart-preview-label">
                          Manufacturer (
                          {values.manufacturers.length +
                            (manufacturerInput.trim() &&
                            manufacturerInput
                              .split(",")
                              .map((m) => m.trim())
                              .filter((m) => m && !values.manufacturers.includes(m)).length > 0
                              ? manufacturerInput
                                  .split(",")
                                  .map((m) => m.trim())
                                  .filter((m) => m && !values.manufacturers.includes(m)).length
                              : 0)}
                          ):
                        </strong>
                        {values.manufacturers.length > 0 ||
                        (manufacturerInput.trim() &&
                          manufacturerInput
                            .split(",")
                            .map((m) => m.trim())
                            .filter((m) => m && !values.manufacturers.includes(m)).length > 0) ? (
                          <div className="d-flex flex-wrap gap-1 mt-1">
                            {values.manufacturers.map((m: string, idx: number) => (
                              <Badge key={idx} className="smart-preview-badge">
                                {m}
                              </Badge>
                            ))}
                            {manufacturerInput.trim() &&
                              manufacturerInput
                                .split(",")
                                .map((m) => m.trim())
                                .filter((m) => m && !values.manufacturers.includes(m))
                                .map((m: string, idx: number) => (
                                  <Badge
                                    key={`pending-${idx}`}
                                    className="smart-preview-badge"
                                  >
                                    {m}
                                  </Badge>
                                ))}
                          </div>
                        ) : (
                          <span className="text-muted"> No Manufacturers added</span>
                        )}
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Footer Actions */}
            <div className="smart-filters-footer">
              <Button
                variant="outline-secondary"
                onClick={handleCancel}
                disabled={isSubmitting}
                className="smart-footer-btn smart-footer-btn-cancel"
              >
                <FontAwesomeIcon icon={faXmark} className="me-2" />
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={isSubmitting || !values.type}
                className="smart-footer-btn smart-footer-btn-save"
              >
                <FontAwesomeIcon icon={faCheck} className="me-2" />
                {isSubmitting ? "Saving..." : "Save"}
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </Container>
  );
};

export default SmartFiltersWizard;
// [2026-07-08] redeploy: re-commit cookie-auth fix to trigger rebuild
